import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, Zap } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import ConsumptionParametersSection from './ConsumptionParametersSection';
import TariffParametersSection from './TariffParametersSection';
import AppliancesSection from './AppliancesSection';
import DiscountSection from './DiscountSection';
import MeterReadingsSection from './MeterReadingsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger, isNumber } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета стоимости электроэнергии
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  russianRegions,
  tariffTypes,
  meterTypes,
  discountTypes,
  appliancesList,
  setAppliancesList,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalcName, setSaveCalcName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем тип счетчика в зависимости от типа тарифа
      if (name === 'tariffType') {
        if (value === 'single') {
          newState.meterType = 'single';
        } else {
          newState.meterType = 'multi';
        }
      }

      // Обновляем поля в зависимости от типа льготы
      if (name === 'discountType') {
        const selectedDiscount = discountTypes.find(type => type.id === value);
        if (selectedDiscount) {
          newState.discountPercent = selectedDiscount.percent.toString();
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'hasDiscount' && !checked) {
        newState.discountType = 'social';
        newState.discountPercent = '50';
      }
      
      if (name === 'hasPreviousReadings' && !checked) {
        newState.previousReading = '';
        newState.currentReading = '';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [discountTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      region, tariffType, meterType, consumptionType,
      totalConsumption, dayConsumption, nightConsumption, peakConsumption, semipeakConsumption, offpeakConsumption,
      calculationDate, hasDiscount, discountPercent, hasPreviousReadings, previousReading, currentReading,
      includeExtraFees, householdMembers, householdArea, socialNorm
    } = formData;

    // Проверка региона
    if (!region) newErrors.region = 'Выберите регион.';

    // Проверка типа тарифа
    if (!tariffType) newErrors.tariffType = 'Выберите тип тарифа.';

    // Проверка типа счетчика
    if (!meterType) newErrors.meterType = 'Выберите тип счетчика.';

    // Проверка типа расчета
    if (!consumptionType) newErrors.consumptionType = 'Выберите тип расчета.';

    // Валидация показаний
    if (consumptionType === 'manual') {
      if (meterType === 'single') {
        const numTotalConsumption = parseFloatOrDefault(totalConsumption, -1);
        if (numTotalConsumption < 0) newErrors.totalConsumption = 'Введите положительное значение.';
        if (!isNumber(totalConsumption) || numTotalConsumption < 0) newErrors.totalConsumption = 'Введите корректное значение потребления.';
      } else if (meterType === 'multi') {
        if (tariffType === 'day_night') {
          const numDayConsumption = parseFloatOrDefault(dayConsumption, -1);
          const numNightConsumption = parseFloatOrDefault(nightConsumption, -1);
          
          if (!isNumber(dayConsumption) || numDayConsumption < 0) newErrors.dayConsumption = 'Введите корректное значение.';
          if (!isNumber(nightConsumption) || numNightConsumption < 0) newErrors.nightConsumption = 'Введите корректное значение.';
        } else if (tariffType === 'zones') {
          const numPeakConsumption = parseFloatOrDefault(peakConsumption, -1);
          const numSemipeakConsumption = parseFloatOrDefault(semipeakConsumption, -1);
          const numOffpeakConsumption = parseFloatOrDefault(offpeakConsumption, -1);
          
          if (!isNumber(peakConsumption) || numPeakConsumption < 0) newErrors.peakConsumption = 'Введите корректное значение.';
          if (!isNumber(semipeakConsumption) || numSemipeakConsumption < 0) newErrors.semipeakConsumption = 'Введите корректное значение.';
          if (!isNumber(offpeakConsumption) || numOffpeakConsumption < 0) newErrors.offpeakConsumption = 'Введите корректное значение.';
        }
      }
    } else if (consumptionType === 'appliances') {
      // Проверка, что выбран хотя бы один прибор
      const activeAppliances = appliancesList.filter(app => app.active);
      if (activeAppliances.length === 0) {
        newErrors.appliances = 'Выберите хотя бы один электроприбор.';
      }

      // Проверка, что количество и часы использования указаны корректно
      for (const appliance of activeAppliances) {
        if (!isPositiveNumber(appliance.quantity) || appliance.quantity <= 0) {
          newErrors.applianceQuantity = 'Количество приборов должно быть положительным.';
        }
        if (!isPositiveNumber(appliance.hoursPerDay) || appliance.hoursPerDay <= 0) {
          newErrors.applianceHours = 'Время использования должно быть положительным.';
        }
      }
    }

    // Проверка даты
    if (!calculationDate) newErrors.calculationDate = 'Укажите дату расчета.';
    try {
      const dateObject = new Date(calculationDate);
      if (isNaN(dateObject.getTime())) {
        newErrors.calculationDate = 'Некорректная дата.';
      }
    } catch (e) {
      newErrors.calculationDate = 'Некорректная дата.';
    }

    // Валидация льгот
    if (hasDiscount) {
      const numDiscountPercent = parseFloatOrDefault(discountPercent, -1);
      if (!isNumber(discountPercent) || numDiscountPercent <= 0 || numDiscountPercent > 100) {
        newErrors.discountPercent = 'Введите значение от 1 до 100%.';
      }
    }

    // Валидация предыдущих показаний
    if (hasPreviousReadings) {
      const numPreviousReading = parseFloatOrDefault(previousReading, -1);
      const numCurrentReading = parseFloatOrDefault(currentReading, -1);
      
      if (!isNumber(previousReading) || numPreviousReading < 0) {
        newErrors.previousReading = 'Введите корректное значение.';
      }
      
      if (!isNumber(currentReading) || numCurrentReading < 0) {
        newErrors.currentReading = 'Введите корректное значение.';
      }
      
      if (numPreviousReading >= numCurrentReading && !newErrors.previousReading && !newErrors.currentReading) {
        newErrors.currentReading = 'Текущие показания должны быть больше предыдущих.';
      }
    }

    // Валидация дополнительных параметров
    if (includeExtraFees) {
      const numHouseholdMembers = parseIntOrDefault(householdMembers, -1);
      const numHouseholdArea = parseFloatOrDefault(householdArea, -1);
      const numSocialNorm = parseFloatOrDefault(socialNorm, -1);
      
      if (!isInteger(householdMembers) || numHouseholdMembers <= 0) {
        newErrors.householdMembers = 'Укажите корректное количество.';
      }
      
      if (!isNumber(householdArea) || numHouseholdArea <= 0) {
        newErrors.householdArea = 'Укажите корректную площадь.';
      }
      
      if (!isNumber(socialNorm) || numSocialNorm < 0) {
        newErrors.socialNorm = 'Укажите корректную социальную норму.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, appliancesList, setErrors]);

  // Функция расчета стоимости электроэнергии
  const calculateElectricityCost = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const {
          region, tariffType, meterType, consumptionType,
          totalConsumption, dayConsumption, nightConsumption, peakConsumption, semipeakConsumption, offpeakConsumption,
          calculationDate, hasDiscount, discountType, discountPercent, hasPreviousReadings, previousReading, currentReading,
          includeExtraFees, householdMembers, householdArea, socialNorm
        } = formData;

        // Получаем тарифы для выбранного региона
        const selectedRegion = russianRegions.find(r => r.id === region);
        if (!selectedRegion) {
          setErrors({ general: "Регион не найден. Пожалуйста, выберите регион из списка." });
          setIsCalculating(false);
          return;
        }

        // Получаем потребление
        let consumption = {
          total: 0,
          day: 0,
          night: 0,
          peak: 0,
          semipeak: 0,
          offpeak: 0
        };

        // Расчет потребления в зависимости от способа ввода
        if (consumptionType === 'manual') {
          if (meterType === 'single') {
            consumption.total = parseFloatOrDefault(totalConsumption, 0);
            // Для аналитики разбиваем по примерным зонам
            if (tariffType === 'day_night') {
              consumption.day = consumption.total * 0.7; // Примерно 70% днем
              consumption.night = consumption.total * 0.3; // Примерно 30% ночью
            } else if (tariffType === 'zones') {
              consumption.peak = consumption.total * 0.3; // Примерно 30% в пик
              consumption.semipeak = consumption.total * 0.5; // Примерно 50% в полупик
              consumption.offpeak = consumption.total * 0.2; // Примерно 20% вне пика
            }
          } else if (meterType === 'multi') {
            if (tariffType === 'day_night') {
              consumption.day = parseFloatOrDefault(dayConsumption, 0);
              consumption.night = parseFloatOrDefault(nightConsumption, 0);
              consumption.total = consumption.day + consumption.night;
            } else if (tariffType === 'zones') {
              consumption.peak = parseFloatOrDefault(peakConsumption, 0);
              consumption.semipeak = parseFloatOrDefault(semipeakConsumption, 0);
              consumption.offpeak = parseFloatOrDefault(offpeakConsumption, 0);
              consumption.total = consumption.peak + consumption.semipeak + consumption.offpeak;
            }
          }
        } else if (consumptionType === 'appliances') {
          // Расчет по бытовым приборам
          const activeAppliances = appliancesList.filter(app => app.active);
          
          // Суммарное потребление по всем приборам
          let totalKwh = 0;
          const appliancesConsumption = [];
          
          activeAppliances.forEach(appliance => {
            const kwhPerMonth = (appliance.power / 1000) * appliance.hoursPerDay * 30 * appliance.quantity * appliance.usageFactor;
            totalKwh += kwhPerMonth;
            
            appliancesConsumption.push({
              id: appliance.id,
              name: appliance.name,
              quantity: appliance.quantity,
              power: appliance.power,
              hoursPerDay: appliance.hoursPerDay,
              kwhPerMonth: kwhPerMonth
            });
          });
          
          consumption.total = totalKwh;
          
          // Разбиваем по зонам на основе средних показателей использования
          if (tariffType === 'day_night') {
            consumption.day = totalKwh * 0.7;
            consumption.night = totalKwh * 0.3;
          } else if (tariffType === 'zones') {
            consumption.peak = totalKwh * 0.3;
            consumption.semipeak = totalKwh * 0.5;
            consumption.offpeak = totalKwh * 0.2;
          }
        }

        // Расчет стоимости электричества в зависимости от типа тарифа
        let costWithoutDiscount = 0;
        let costBreakdown = {};
        
        if (tariffType === 'single') {
          costWithoutDiscount = consumption.total * selectedRegion.singleRate;
          costBreakdown = {
            single: {
              consumption: consumption.total,
              rate: selectedRegion.singleRate,
              cost: costWithoutDiscount
            }
          };
        } else if (tariffType === 'day_night') {
          const dayCost = consumption.day * selectedRegion.dayRate;
          const nightCost = consumption.night * selectedRegion.nightRate;
          costWithoutDiscount = dayCost + nightCost;
          costBreakdown = {
            day: {
              consumption: consumption.day,
              rate: selectedRegion.dayRate,
              cost: dayCost
            },
            night: {
              consumption: consumption.night,
              rate: selectedRegion.nightRate,
              cost: nightCost
            }
          };
        } else if (tariffType === 'zones') {
          const peakCost = consumption.peak * selectedRegion.peakRate;
          const semipeakCost = consumption.semipeak * selectedRegion.semipeakRate;
          const offpeakCost = consumption.offpeak * selectedRegion.offpeakRate;
          costWithoutDiscount = peakCost + semipeakCost + offpeakCost;
          costBreakdown = {
            peak: {
              consumption: consumption.peak,
              rate: selectedRegion.peakRate,
              cost: peakCost
            },
            semipeak: {
              consumption: consumption.semipeak,
              rate: selectedRegion.semipeakRate,
              cost: semipeakCost
            },
            offpeak: {
              consumption: consumption.offpeak,
              rate: selectedRegion.offpeakRate,
              cost: offpeakCost
            }
          };
        }

        // Расчет стоимости с учетом льгот
        let finalCost = costWithoutDiscount;
        let discountAmount = 0;
        let discountInfo = null;
        
        if (hasDiscount) {
          const discountPct = parseFloatOrDefault(discountPercent, 0);
          discountAmount = costWithoutDiscount * (discountPct / 100);
          finalCost = costWithoutDiscount - discountAmount;
          
          const selectedDiscountType = discountTypes.find(d => d.id === discountType);
          discountInfo = {
            type: selectedDiscountType?.name || 'Льгота',
            percent: discountPct,
            amount: discountAmount
          };
        }

        // Расчет дополнительных показателей
        let extraInfo = null;
        
        if (hasPreviousReadings) {
          const prevReading = parseFloatOrDefault(previousReading, 0);
          const currReading = parseFloatOrDefault(currentReading, 0);
          const actualConsumption = currReading - prevReading;
          
          extraInfo = {
            ...(extraInfo || {}),
            meterReadings: {
              previous: prevReading,
              current: currReading,
              difference: actualConsumption,
              inputTotal: consumption.total,
              deviation: actualConsumption - consumption.total
            }
          };
        }

        if (includeExtraFees) {
          const numHouseholdMembers = parseIntOrDefault(householdMembers, 1);
          const numArea = parseFloatOrDefault(householdArea, 0);
          const numSocialNorm = parseFloatOrDefault(socialNorm, 0);
          
          let extraFees = 0;
          let normExceeded = false;
          let normExceedAmount = 0;
          
          // Если превышена социальная норма, добавляем повышенный тариф к излишкам
          if (numSocialNorm > 0 && consumption.total > numSocialNorm) {
            normExceeded = true;
            normExceedAmount = consumption.total - numSocialNorm;
            // Примерный повышенный тариф +30% к базовому
            const increasedRate = selectedRegion.singleRate * 1.3;
            extraFees = normExceedAmount * (increasedRate - selectedRegion.singleRate);
          }
          
          extraInfo = {
            ...(extraInfo || {}),
            household: {
              members: numHouseholdMembers,
              area: numArea,
              socialNorm: numSocialNorm,
              normExceeded: normExceeded,
              normExceedAmount: normExceedAmount,
              extraFees: extraFees
            }
          };
          
          finalCost += extraFees;
        }

        // Экономия при использовании многотарифного учета
        let savings = 0;
        if (tariffType !== 'single') {
          // Расчет, если бы использовался однотарифный учет
          const singleTariffCost = consumption.total * selectedRegion.singleRate;
          savings = singleTariffCost - costWithoutDiscount;
        }

        // Формируем результаты
        const calcResults = {
          totalConsumption: consumption.total,
          costWithoutDiscount: costWithoutDiscount,
          finalCost: finalCost,
          costBreakdown: costBreakdown,
          discountInfo: discountInfo,
          extraInfo: extraInfo,
          consumption: consumption,
          savings: savings,
          regionInfo: selectedRegion,
          tariffType: tariffType,
          calculationDate: new Date(calculationDate),
          appliancesConsumption: consumptionType === 'appliances' ? 
            appliancesList.filter(app => app.active).map(app => ({
              id: app.id,
              name: app.name,
              power: app.power,
              hoursPerDay: app.hoursPerDay,
              quantity: app.quantity,
              consumption: (app.power / 1000) * app.hoursPerDay * 30 * app.quantity * app.usageFactor,
              percent: ((app.power / 1000) * app.hoursPerDay * 30 * app.quantity * app.usageFactor / consumption.total) * 100
            })) : null
        };

        setResults(calcResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, russianRegions, discountTypes, appliancesList, setErrors, setResults, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      region: 'moscow',
      tariffType: 'single',
      meterType: 'single',
      consumptionType: 'manual',
      totalConsumption: '250',
      dayConsumption: '180',
      nightConsumption: '70',
      peakConsumption: '100',
      semipeakConsumption: '100',
      offpeakConsumption: '50',
      calculationDate: new Date().toISOString().split('T')[0],
      hasDiscount: false,
      discountType: 'social',
      discountPercent: '50',
      hasPreviousReadings: false,
      previousReading: '5750',
      currentReading: '6000',
      includeExtraFees: false,
      householdMembers: '1',
      householdArea: '50',
      socialNorm: '500',
    });
    setResults(null);
    setErrors({});
    setSaveCalcName('');
    setShowSaveDialog(false);
    
    // Сбрасываем список приборов
    setAppliancesList(prev => prev.map(appliance => ({
      ...appliance,
      active: false,
      quantity: 1
    })));
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Категория', 'Значение'];
    
    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');
    
    const selectedRegion = russianRegions.find(r => r.id === formData.region);
    const regionName = selectedRegion ? selectedRegion.name : formData.region;
    
    const rows = [
      ['Регион', regionName],
      ['Тип тарифа', tariffTypes.find(t => t.id === formData.tariffType)?.name || formData.tariffType],
      ['Тип счетчика', meterTypes.find(m => m.id === formData.meterType)?.name || formData.meterType],
      ['Дата расчета', new Date(formData.calculationDate).toLocaleDateString('ru-RU')],
      [''],
      ['Потребление (кВт⋅ч)', formatCsvNumber(results.totalConsumption)],
    ];
    
    // Данные по тарифам
    if (formData.tariffType === 'single') {
      rows.push(['Тариф (₽/кВт⋅ч)', formatCsvNumber(results.regionInfo.singleRate)]);
    } else if (formData.tariffType === 'day_night') {
      rows.push(['Дневной тариф (₽/кВт⋅ч)', formatCsvNumber(results.regionInfo.dayRate)]);
      rows.push(['Ночной тариф (₽/кВт⋅ч)', formatCsvNumber(results.regionInfo.nightRate)]);
      rows.push(['Дневное потребление (кВт⋅ч)', formatCsvNumber(results.consumption.day)]);
      rows.push(['Ночное потребление (кВт⋅ч)', formatCsvNumber(results.consumption.night)]);
    } else if (formData.tariffType === 'zones') {
      rows.push(['Пиковый тариф (₽/кВт⋅ч)', formatCsvNumber(results.regionInfo.peakRate)]);
      rows.push(['Полупиковый тариф (₽/кВт⋅ч)', formatCsvNumber(results.regionInfo.semipeakRate)]);
      rows.push(['Внепиковый тариф (₽/кВт⋅ч)', formatCsvNumber(results.regionInfo.offpeakRate)]);
      rows.push(['Пиковое потребление (кВт⋅ч)', formatCsvNumber(results.consumption.peak)]);
      rows.push(['Полупиковое потребление (кВт⋅ч)', formatCsvNumber(results.consumption.semipeak)]);
      rows.push(['Внепиковое потребление (кВт⋅ч)', formatCsvNumber(results.consumption.offpeak)]);
    }

    rows.push(['']);
    rows.push(['Стоимость без льгот (₽)', formatCsvNumber(results.costWithoutDiscount)]);
    
    if (results.discountInfo) {
      rows.push(['Тип льготы', results.discountInfo.type]);
      rows.push(['Процент льготы (%)', formatCsvNumber(results.discountInfo.percent)]);
      rows.push(['Сумма льготы (₽)', formatCsvNumber(results.discountInfo.amount)]);
    }
    
    if (results.extraInfo?.household) {
      rows.push(['Количество проживающих', results.extraInfo.household.members]);
      rows.push(['Площадь жилья (м²)', formatCsvNumber(results.extraInfo.household.area)]);
      rows.push(['Социальная норма (кВт⋅ч)', formatCsvNumber(results.extraInfo.household.socialNorm)]);
      
      if (results.extraInfo.household.normExceeded) {
        rows.push(['Превышение социальной нормы (кВт⋅ч)', formatCsvNumber(results.extraInfo.household.normExceedAmount)]);
        rows.push(['Дополнительная плата (₽)', formatCsvNumber(results.extraInfo.household.extraFees)]);
      }
    }
    
    rows.push(['']);
    rows.push(['ИТОГОВАЯ СТОИМОСТЬ (₽)', formatCsvNumber(results.finalCost)]);
    
    if (results.savings > 0) {
      rows.push(['Экономия по сравнению с однотарифным учетом (₽)', formatCsvNumber(results.savings)]);
    }
    
    // Если есть данные по приборам, добавляем их
    if (results.appliancesConsumption && results.appliancesConsumption.length > 0) {
      rows.push(['']);
      rows.push(['ПОТРЕБЛЕНИЕ ПО ПРИБОРАМ']);
      rows.push(['Наименование', 'Мощность (Вт)', 'Кол-во', 'Время (ч/день)', 'Потребление (кВт⋅ч)', 'Доля (%)']);
      
      results.appliancesConsumption.forEach(appliance => {
        rows.push([
          appliance.name,
          appliance.power,
          appliance.quantity,
          appliance.hoursPerDay,
          formatCsvNumber(appliance.consumption),
          formatCsvNumber(appliance.percent)
        ]);
      });
    }
    
    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + rows.map(row => row.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_электроэнергии.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalcName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalcName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateElectricityCost();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Zap className="w-6 h-6 text-yellow-500" />
          Параметры расчета электроэнергии
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Параметры тарифа и региона */}
        <TariffParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          russianRegions={russianRegions}
          tariffTypes={tariffTypes}
          meterTypes={meterTypes}
        />

        {/* Параметры потребления */}
        <ConsumptionParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Раздел с бытовыми приборами */}
        {formData.consumptionType === 'appliances' && (
          <AppliancesSection 
            appliancesList={appliancesList}
            setAppliancesList={setAppliancesList}
            errors={errors}
          />
        )}

        {/* Раздел с льготами */}
        <DiscountSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          discountTypes={discountTypes}
        />

        {/* Показания счетчика */}
        <MeterReadingsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalcName}
                onChange={(e) => setSaveCalcName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Расчет...' : 'Рассчитать стоимость'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;